import React from 'react';
import { observer } from 'mobx-react-lite';

import mean from 'lodash/mean';

import eachDay from 'date-fns/each_day';
import format from 'date-fns/format';
import startOfDay from 'date-fns/start_of_day';

import { BarChart, Label, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

import { ChartColours } from '../../ChartColours';
import ObservableAutoSizer from '@vromo/ui/components/ObservableAutoSizer';
import { useStore } from '@vromo/mobx-react';
import { Job } from '@vromo/models';
import convertDistance from '@vromo/utils/convertDistance';
import { RootStore } from '@vromo/dashboard/src/stores/RootStore';
import { DefaultTooltipContent } from 'recharts/lib/component/DefaultTooltipContent';

const CustomTooltip = (props: any) => {
    const { viewStore } = useStore<RootStore>('rootStore');

    if (props.payload && props.payload[0] != null) {
        const formatDist = (num: number) =>
            `${num?.toFixed(1)} ${viewStore.imperialUnits ? 'mi' : 'km'}`;

        const newPayload = [
            {
                color: ChartColours[4],
                dataKey: 'Total Distance',
                name: 'Total Distance',
                value: formatDist(
                    props.payload[0].payload['Off Job Distance'] +
                    props.payload[0].payload['On Job Distance']
                )
            },
            ...props.payload
                .slice()
                .reverse()
                .map((p: any) => ({
                    ...p,
                    value: formatDist(p.value)
                }))
        ];

        return (
            <DefaultTooltipContent
                {...props}
                payload={newPayload}
                labelFormatter={(label: number) => format(new Date(label), 'dddd Do MMMM YYYY')}
            />
        );
    }
    return <DefaultTooltipContent {...props} />;
};

type Props = {
    jobs: Job[];
    startDate: Date;
    endDate: Date;
};

export default observer<Props>(function DrivingDistanceChart({ jobs, startDate, endDate }) {
    const { viewStore } = useStore<RootStore>('rootStore');

    const dates = eachDay(startDate, endDate);

    const data = dates.map((d) => {
        const offJobDistances: number[] = [];
        const onJobDistances: number[] = [];

        jobs.filter(
            (j) =>
                j.status == 'completed' &&
                startOfDay(j.finished!).getTime() == d.getTime() &&
                j.route != null
        ).forEach((j) => {
            if (j.route!.items.length) {
                const routes = j.route!.items;

                const offJobDist = routes.find((r) => r.ix == 0)!.distance;

                const onJobDist = routes
                    .filter((r) => r.ix != 0)
                    .reduce((acc, r) => {
                        const routeLegDistance = r.distance || 0;
                        return acc + routeLegDistance;
                    }, 0);

                if (offJobDist) offJobDistances.push(offJobDist);
                if (onJobDist) onJobDistances.push(onJobDist);
            }
        });

        return {
            date: format(d, 'DD MMMM YYYY'),
            'Off Job Distance': offJobDistances.length
                ? convertDistance(mean(offJobDistances), viewStore.imperialUnits).distance()
                : 0,
            'On Job Distance': onJobDistances.length
                ? convertDistance(mean(onJobDistances), viewStore.imperialUnits).distance()
                : 0
        };
    });

    return (
        <ObservableAutoSizer customChildren={({ width }: { width: number }) => (
            <BarChart
                width={width}
                height={220}
                data={data}
                margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5
                }}>
                <CartesianGrid strokeDasharray="3 5" />
                <XAxis
                    dataKey="date"
                    minTickGap={100}
                    tickFormatter={(t) => format(new Date(t), 'Do MMM')}
                />
                <YAxis>
                    <Label
                        value={`Driving Distance (${viewStore.imperialUnits ? 'mi' : 'km'})`}
                        angle={-90}
                        position={'insideLeft'}
                        offset={15}
                        style={{ textAnchor: 'middle', fontSize: '11px' }}
                    />
                </YAxis>
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="Off Job Distance" stackId="a" fill={ChartColours[0]} />
                <Bar dataKey="On Job Distance" stackId="a" fill={ChartColours[2]} />
            </BarChart>
        )} />
    );
});
